import {
  clampTransitionDuration,
  isDirectionalKind,
  isTransformKind,
  isValidDirection,
  type KeyframeTransition,
  type SceneTransition,
  type TransitionKind,
} from './types';
import type { CutWindow, SceneLayoutBlock, SceneLike } from './buildSceneLayout';
import { sourceTimeToOutputTime } from './mapTimes';

/** Snap tolerance (seconds) for matching a persisted transition's atTime to a
 *  current keyframe boundary. Loose enough to survive rounding + tiny
 *  keyframe nudges, tight enough not to jump to the wrong boundary. */
export const KEYFRAME_TRANSITION_EPS = 0.075;

/** Synthetic cut-index base: keyframe cut windows get NEGATIVE indexes so they
 *  can never collide with interval cut indexes (which are >= 0). */
export const KEYFRAME_CUT_INDEX_BASE = -1000;

/** Round to 3 decimals — the write-time precision for a boundary atTime. */
export const round3 = (n: number): number => Math.round(n * 1000) / 1000;

interface KeyframeLike {
  time: number;
  endTime: number;
}

/** The boundary time between each consecutive keyframe pair — the midpoint of
 *  kf[i].endTime and kf[i+1].time (they are contiguous, so usually equal). */
export function keyframeBoundaries(keyframes: KeyframeLike[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < keyframes.length - 1; i++) {
    out.push((keyframes[i].endTime + keyframes[i + 1].time) / 2);
  }
  return out;
}

/** Validate a persisted/unknown keyframeTransitions blob against the CURRENT
 *  boundary list: transform-family kinds only, snap each atTime to the nearest
 *  boundary within EPS (dropping non-matches), clamp durations, dedupe by
 *  boundary (first wins), keep direction only on directional kinds, sorted by
 *  atTime. Time-keyed, so it reconciles across keyframe edits with no index
 *  remap. */
export function sanitizeKeyframeTransitions(
  raw: unknown,
  boundaries: number[],
): KeyframeTransition[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<number>();
  const out: KeyframeTransition[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const t = item as Record<string, unknown>;
    if (typeof t.kind !== 'string' || !isTransformKind(t.kind as TransitionKind)) continue;
    if (typeof t.atTime !== 'number' || !Number.isFinite(t.atTime)) continue;

    let best: number | null = null;
    let bestDist = KEYFRAME_TRANSITION_EPS;
    for (const b of boundaries) {
      const d = Math.abs(b - t.atTime);
      if (d < bestDist) {
        bestDist = d;
        best = b;
      }
    }
    if (best === null) continue;
    const key = round3(best);
    if (seen.has(key)) continue;
    seen.add(key);

    const kind = t.kind as TransitionKind;
    const kt: KeyframeTransition = {
      atTime: key,
      kind,
      durationSec: clampTransitionDuration(typeof t.durationSec === 'number' ? t.durationSec : NaN),
    };
    if (isDirectionalKind(kind) && isValidDirection(t.direction)) {
      kt.direction = t.direction;
    }
    out.push(kt);
  }
  return out.sort((a, b) => a.atTime - b.atTime);
}

/** Build a transform CutWindow around a boundary frame, clamped to [minFrame,
 *  maxFrame]. Returns null when the window would be empty. Shared by the
 *  source-time (preview) and output-time (render) derivations. */
function transformWindow(
  transition: SceneTransition,
  boundaryFrame: number,
  minFrame: number,
  maxFrame: number,
  fps: number,
  index: number,
): CutWindow | null {
  const want = Math.round(clampTransitionDuration(transition.durationSec) * fps);
  const halfA = Math.min(Math.ceil(want / 2), boundaryFrame - minFrame);
  const halfB = Math.min(Math.floor(want / 2), maxFrame - boundaryFrame);
  if (halfA + halfB <= 0) return null;
  return {
    index,
    transition,
    startFrame: boundaryFrame - halfA,
    endFrame: boundaryFrame + halfB,
    boundaryFrame,
    overlapFrames: 0,
  };
}

const toSceneTransition = (t: KeyframeTransition, ordinal: number): SceneTransition => ({
  afterSceneIndex: ordinal,
  kind: t.kind,
  durationSec: t.durationSec,
  ...(t.direction ? { direction: t.direction } : {}),
});

/** Preview derivation: map each keyframe transition's atTime (SOURCE seconds)
 *  to an output boundary frame via sourceTimeToOutputTime, keep only boundaries
 *  that fall STRICTLY inside a block's kept range (so a cut can never straddle
 *  an interval edge), and build a transform window clamped to the containing
 *  block. */
export function deriveKeyframeCutWindows(
  { transitions, scenes, blocks, fps }:
  { transitions: KeyframeTransition[]; scenes: SceneLike[]; blocks: SceneLayoutBlock[]; fps: number },
): CutWindow[] {
  const cuts: CutWindow[] = [];
  transitions.forEach((t, i) => {
    const boundaryFrame = Math.round(sourceTimeToOutputTime(scenes, blocks, t.atTime, fps) * fps);
    const block = blocks.find(
      (b) => boundaryFrame > b.from && boundaryFrame < b.from + b.durationInFrames,
    );
    if (!block) return;
    const win = transformWindow(
      toSceneTransition(t, i),
      boundaryFrame,
      block.from,
      block.from + block.durationInFrames,
      fps,
      KEYFRAME_CUT_INDEX_BASE - i,
    );
    if (win) cuts.push(win);
  });
  return cuts;
}

export interface KeyframeTransitionOutput {
  atTimeOutput: number | null;
  kind: TransitionKind;
  durationSec: number;
  direction?: unknown;
}

/** Render derivation: the backend already mapped each transition's boundary to
 *  OUTPUT seconds (atTimeOutput). Map to a frame, keep only boundaries strictly
 *  inside the composition, and build a transform window clamped to [0,
 *  totalFrames]. Robust against overlap kinds / null atTimeOutput echoed by an
 *  older/permissive backend. */
export function deriveKeyframeCutWindowsFromOutput(
  transitions: KeyframeTransitionOutput[],
  totalFrames: number,
  fps: number,
): CutWindow[] {
  if (!Array.isArray(transitions)) return [];
  const cuts: CutWindow[] = [];
  transitions.forEach((t, i) => {
    if (!t || typeof t.kind !== 'string' || !isTransformKind(t.kind)) return;
    if (typeof t.atTimeOutput !== 'number' || !Number.isFinite(t.atTimeOutput)) return;
    const boundaryFrame = Math.round(t.atTimeOutput * fps);
    if (boundaryFrame <= 0 || boundaryFrame >= totalFrames) return;
    const transition: SceneTransition = {
      afterSceneIndex: i,
      kind: t.kind,
      durationSec: t.durationSec,
      ...(isDirectionalKind(t.kind) && isValidDirection(t.direction) ? { direction: t.direction } : {}),
    };
    const win = transformWindow(transition, boundaryFrame, 0, totalFrames, fps, KEYFRAME_CUT_INDEX_BASE - i);
    if (win) cuts.push(win);
  });
  return cuts;
}
